/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// utils
import UuidUtils from "utils/uuid-utils/UuidUtils.js";
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

// cached functions
const hasProperty = Object.prototype.hasOwnProperty;

class PublishSubscribeUtils {
  static topics = {};

  /**
  * @param {Object} metadata
  * @param {String} name
  * @returns {String}
  */
  static createTopic({
    metadata = {},
    name = String("not-set"),
  }) {
    if (!ValidationUtils.string(name)) {
      throw Error(`Cannot create topic, name does not pass validation (string): "${name}".`);
    }
    const topicId = UuidUtils.uuid(Object.keys(PublishSubscribeUtils.topics));
    const topic = {
      id: String(topicId),
      subscribers: {},
      metadata,
      name,
    };
    PublishSubscribeUtils.topics[topicId] = topic;
    return String(topicId);
  }

  /**
  * @param {String} topicId
  * @param {Object} payload
  * @returns {Boolean}
  * @throws {Error}
  */
  static publish(topicId, payload) {
    // topic
    if (!ValidationUtils.string(topicId)) {
      throw Error(`Cannot publish payload, topicId does not pass validation (string): "${topicId}".`);
    }
    if(!hasProperty.call(PublishSubscribeUtils.topics, topicId)) {
      throw Error(`Cannot publish payload, no topic exists with topicId: "${topicId}".`);
    }
    const topic = PublishSubscribeUtils.topics[topicId];

    // payload
    if (!ValidationUtils.exists(payload)) {
      throw Error(`Cannot publish payload, payload does not pass validation (exists): "${payload}".`);
    }

    // publish payload to subscribers
    const subscribers = topic.subscribers;
    let payloadPublished = Boolean(false);
    for (const subscriberId in subscribers) {
      if (
        subscribers[subscriberId] &&
        hasProperty.call(subscribers[subscriberId], "onPayload")
      ) {
        subscribers[subscriberId].onPayload(payload);
        payloadPublished = Boolean(true);
      } else {
        PublishSubscribeUtils.unsubscribe(topicId, subscriberId);
      }
    }
    return payloadPublished;
  }

  /**
  * @param {String} topicId
  * @param {Function} onPayload
  * @param {Function} onUnsubscribe
  * @returns {String}
  * @throws {Error}
  */
  static subscribe(topicId, onPayload, onUnsubscribe) {
    // topic
    if (!ValidationUtils.string(topicId)) {
      throw Error(`Cannot subscribe to topic, topicId does not pass validation (string): "${topicId}".`);
    }
    if(!hasProperty.call(PublishSubscribeUtils.topics, topicId)) {
      throw Error(`Cannot subscribe to topic, no topic exists with topicId: "${topicId}".`);
    }
    const topic = PublishSubscribeUtils.topics[topicId];

    // onPayload
    if (!(onPayload instanceof Function)) {
      throw Error(`Cannot subscribe to topic, invalid subscription payload callback.`);
    }

    // onUnsubscribe
    if (ValidationUtils.exists(onUnsubscribe) && !(onUnsubscribe instanceof Function)) {
      throw Error(`Cannot subscribe to topic, invalid subscription unsubscribe callback.`);
    }

    // subscriber
    const subscriberId = UuidUtils.uuid(Object.keys(topic.subscribers));
    topic.subscribers[subscriberId] = {
      id: subscriberId,
      onPayload,
      onUnsubscribe,
    };
    return subscriberId;
  }

  /**
  * @param {String} topicId
  * @param {String} subscriberId
  */
  static unsubscribe(topicId, subscriberId) {
    // topic
    if (!ValidationUtils.string(topicId)) {
      throw Error(`Cannot unsubscribe from topic, topicId does not pass validation (string): "${topicId}".`);
    }
    if(!hasProperty.call(PublishSubscribeUtils.topics, topicId)) {
      throw Error(`Cannot unsubscribe from topic, no topic exists with topicId: "${topicId}".`);
    }
    const topic = PublishSubscribeUtils.topics[topicId];

    // subscriber
    if (!ValidationUtils.string(subscriberId)) {
      throw Error(`Cannot unsubscribe from topic, subscriberId does not pass validation (subscriberId): "${subscriberId}".`);
    }
    if(!hasProperty.call(topic.subscribers, subscriberId)) {
      throw Error(`Cannot unsubscribe from topic, no subscriber exists with subscriberId: "${subscriberId}".`);
    }
    const subscriber = topic.subscribers[subscriberId];

    // optional unsubscribe callback
    if(hasProperty.call(subscriber, "onUnsubscribe") && subscriber.onUnsubscribe instanceof Function) {
      subscriber.onUnsubscribe();
    }

    // delete subscriber
    delete topic.subscribers[subscriberId];
  }

}
export default PublishSubscribeUtils;
